import type { ListParams } from '../type';
import baseKey from './_baseKey';

/**
 * 작업 티켓 유형
 */
export type TicketType = 'periodic' | 'onetime';

const ticketDomainKey = (ticketSubDomains: (string | number)[]) =>
  baseKey.domainKey('ticket', ticketSubDomains);

/**
 * 작업 티켓 관련 쿼리키 제너리이터
 */
const ticketKey = {
  /**
   * @returns 작업 티켓 도메인 기본 쿼리키 (getter)
   * @example ['ticket']
   */
  domain(ticketType: TicketType) {
    return ticketDomainKey([ticketType]);
  },
  /**
   * @returns 작업 티켓 리스트 도메인 기본 쿼리키
   * @example ['ticket', 'periodic', 'list']
   */
  list(ticketType: TicketType) {
    return [...ticketDomainKey([ticketType]), 'list'];
  },
  /**
   * @param ticketType 작업 티켓 유형
   * @param listParams 리스트 파라미터
   * @returns 전체 작업 티켓 리스트 요청 쿼리키
   * @example ['ticket', 'periodic', 'list', 'all', {...listParams}]
   */
  listAll(ticketType: TicketType, listParams?: ListParams) {
    const coreKey = baseKey.listKey(ticketDomainKey([ticketType]), 'all');
    return listParams ? [...coreKey, listParams] : coreKey;
  },
  /**
   * @param ticketType 작업 티켓 유형
   * @param listParams 리스트 파라미터
   * @returns 페이지네이션 방식 작업 티켓 리스트 요청 쿼리키
   * @example ['ticket', 'periodic', 'list', 'page', {...listParams}]
   */
  listByPage(ticketType: TicketType, listParams?: ListParams) {
    const coreKey = baseKey.listKey(ticketDomainKey([ticketType]), 'page');
    return listParams ? [...coreKey, listParams] : coreKey;
  },
  /**
   * @param ticketType 작업 티켓 유형
   * @param listParams 리스트 파라미터
   * @returns 무한 스크롤 방식 작업 티켓 리스트 요청 쿼리키
   * @example ['ticket', 'periodic', 'list', 'scroll', {...listParams}]
   */
  listByScroll(ticketType: TicketType, listParams?: ListParams) {
    const coreKey = baseKey.listKey(ticketDomainKey([ticketType]), 'scroll');
    return listParams ? [...coreKey, listParams] : coreKey;
  },
  /**
   * @param ticketType 작업 티켓 유형
   * @param listParams 리스트 파라미터
   * @returns 작업 티켓의 상태별 통계 리스트 요청 쿼리키
   * @example ['ticket', 'periodic', 'statistics', 'all', {...listParams}]
   */
  statistics(ticketType: TicketType, listParams?: ListParams) {
    const coreKey = baseKey.statisticsKey(ticketDomainKey([ticketType]), 'all');
    return listParams ? [...coreKey, listParams] : coreKey;
  },
  /**
   * @param ticketType 작업 티켓 유형
   * @returns 작업 티켓 상세 도메인 기본 쿼리키
   * @example ['ticket', 'periodic', 'detail']
   */
  detail(ticketType: TicketType) {
    return [...ticketDomainKey([ticketType]), 'detail'];
  },
  /**
   * @param ticketType 작업 티켓 유형
   * @param ticketId 작업 티켓 ID
   * @param ticketDate 작업 티켓 날짜 (선택)
   * @returns 작업 티켓 상세 데이터 요청 쿼리키
   * @example ['ticket', 'periodic', 'detail', 1, '2022-08-15']
   */
  detailById(ticketType: TicketType, ticketId: number, ticketDate?: string) {
    const coreKey = [
      ...baseKey.itemKey(ticketDomainKey([ticketType]), 'detail'),
      ticketId,
    ];
    return ticketDate ? [...coreKey, ticketDate] : coreKey;
  },
};

export default Object.freeze(ticketKey);
